import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthenticatedApiRequest from "../../AuthenticatedApiRequest";
import TchrURL from "../../TchrURL";
import { UserSettings } from "../../types/user";
import { toast } from "@/hooks/use-toast";
import { useInvalidateUserStats } from "./useUserStats";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useTranslation } from "react-i18next";
import { getBrowserPreferredLanguage } from "../../utils/browserLanguage";
import {
  A11ySettings,
  translateA11yLanguageToI18next,
  translateLanguageNameToShortCode,
} from "../../contexts/A11yProvider";
import { useResponseChecker } from "@/hooks/useResponseChecker";

const useUserSettings = () => {
  const { get, post } = useAuthenticatedApiRequest();
  const queryClient = useQueryClient();
  const invalidateUserStats = useInvalidateUserStats();
  const { t } = useTranslation("common");
  const { checkResponse } = useResponseChecker("userSettings");

  const [_, setLocalA11ySettings] = useLocalStorage<A11ySettings>(
    "a11y-settings",
    {
      darkMode: false,
      highContrast: false,
      lowAnimation: false,
      dyslexiaFont: false,
      largeText: false,
      language: getBrowserPreferredLanguage(),
    }
  );

  const getUserSettings = async (): Promise<UserSettings> => {
    const url = new TchrURL("/user/settings");

    const response = await get(url, {
      headersOpts: {
      }
    });

    checkResponse(response);

    const userSettings = await response.json();

    if (userSettings?.preferences) {
      const a11ySettings: A11ySettings = {
        darkMode: userSettings.preferences.darkMode ?? false,
        highContrast: userSettings.preferences.highContrast ?? false,
        lowAnimation: userSettings.preferences.lowAnimation ?? false,
        dyslexiaFont: userSettings.preferences.dyslexiaFont ?? false,
        largeText: userSettings.preferences.largeText ?? false,
        language:
          translateA11yLanguageToI18next(
            translateLanguageNameToShortCode(
              userSettings.displayLanguage ?? userSettings.language ?? "english"
            ) || "en-US"
          ) || "en-US",
      };

      // Update localStorage with the user's preferences
      setLocalA11ySettings(a11ySettings);
    }

    return userSettings;
  };

  const updateUserSettings = async (
    body: UserSettings
  ): Promise<UserSettings> => {
    const url = new TchrURL("/user/settings");
    const response = await post(url, JSON.stringify(body));
    return response.json();
  };

  const useGetUserSettings = ({ enabled = true }: { enabled?: boolean }) =>
    useQuery({
      queryKey: ["user", "settings"],
      queryFn: getUserSettings,
      retry: 2,
      enabled,
    });

  const useUpdateUser = () =>
    useMutation({
      mutationKey: ["user", "settings", "update"],
      mutationFn: updateUserSettings,
      onSuccess: (updatedUserSettings) => {
        toast({
          title: t("toast.userSettings.success.title"),
          description: t("toast.userSettings.success.description"),
        });

        if (updatedUserSettings?.preferences) {
          const a11ySettings: A11ySettings = {
            darkMode: updatedUserSettings.preferences.darkMode ?? false,
            highContrast: updatedUserSettings.preferences.highContrast ?? false,
            lowAnimation: updatedUserSettings.preferences.lowAnimation ?? false,
            dyslexiaFont: updatedUserSettings.preferences.dyslexiaFont ?? false,
            largeText: updatedUserSettings.preferences.largeText ?? false,
            language:
              translateLanguageNameToShortCode(
                updatedUserSettings.displayLanguage ??
                updatedUserSettings.language ??
                "english"
              ) || "en-US",
          };

          setLocalA11ySettings(a11ySettings);
        }

        queryClient.invalidateQueries({ queryKey: ["user", "settings"] });
        queryClient.refetchQueries({ queryKey: ["user"] });
        invalidateUserStats();
      },
    });

  return { useGetUserSettings, useUpdateUser };
};

export default useUserSettings;
